import { PlusIcon, UsersIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { ApiError, type RoomListItem, createRoom, getMyRooms } from "../lib/api";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

/**
 * Wspólne oglądanie na stronie głównej.
 *
 * Powstało z konkretnego zgłoszenia: „udostępnianie wspólnego oglądania powinno
 * być łatwo dostępne, teraz go nie widzę". Sedno okazało się głębsze niż brak
 * przycisku — **nie było jak wrócić do pokoju**. Trasy listującej pokoje nie było
 * w ogóle, więc adres znało się tylko dopóki karta pozostawała otwarta.
 *
 * Sekcja jest na stronie głównej, a nie w nawigacji, z dwóch powodów: pokoje są
 * czymś, co się ma albo nie (nie zawsze), a pasek nawigacji ma już sześć pozycji
 * i siódma zaczyna go rozsypywać na telefonie.
 */

export function RoomsRail({ enabled }: { enabled: boolean }) {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<RoomListItem[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setRooms(null);
      return;
    }
    let cancelled = false;
    getMyRooms()
      .then((data) => {
        if (!cancelled) setRooms(data.items);
      })
      // Cisza: sekcja jest dodatkiem do strony głównej, a nie jej treścią.
      .catch(() => setRooms([]));
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const create = useCallback(async () => {
    setCreating(true);
    setError(null);
    try {
      /**
       * Pusty pokój z domyślną nazwą.
       *
       * Materiał dokłada się już w środku (panel „Dodaj do kolejki"), więc pytanie
       * o nazwę przed wejściem byłoby krokiem, który nikogo nie przybliża do
       * oglądania. Nazwę i tak widzi tylko zapraszający.
       */
      const room = await createRoom({ name: "Wspólne oglądanie" });
      navigate(`/pokoj/${room.slug}`);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : "Nie udało się utworzyć pokoju");
    } finally {
      setCreating(false);
    }
  }, [navigate]);

  if (!enabled) return null;

  return (
    <section className="min-w-0">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
          Wspólne oglądanie
        </h2>
        <Button variant="outline" size="sm" onClick={() => void create()} disabled={creating}>
          <PlusIcon />
          {creating ? "Tworzę…" : "Nowy pokój"}
        </Button>
      </div>

      {error !== null && <p className="mb-2 text-sm text-destructive">{error}</p>}

      {rooms !== null && rooms.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nie masz jeszcze pokoju. Utwórz go tutaj albo kliknij „Razem" przy odcinku — w środku
          dostaniesz link do zaproszenia.
        </p>
      ) : (
        <div className="rail-scroll bleed pb-2">
          <div className="flex gap-3">
            {(rooms ?? []).map((room) => (
              <Link
                key={room.slug}
                to={`/pokoj/${room.slug}`}
                className="focus-ring-own flex w-[14rem] shrink-0 flex-col gap-1.5 rounded-xl border border-border bg-card p-3 transition-colors duration-150 outline-none hover:border-primary/40 focus-visible:ring-[0.1875rem] focus-visible:ring-ring/50"
              >
                <span className="truncate font-medium">{room.name}</span>
                <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  {/*
                    Liczba obecnych jest tu najważniejsza: pokój, w którym ktoś
                    siedzi, to zaproszenie do wejścia, a pusty — do zaproszenia kogoś.
                  */}
                  {room.present > 0 ? (
                    <Badge variant="success">
                      <UsersIcon />
                      {room.present} w środku
                    </Badge>
                  ) : (
                    <span>pusty</span>
                  )}
                  {room.isTemporary && <Badge variant="outline">tymczasowy</Badge>}
                  {/*
                    Termin sprzątania przy pustym pokoju. Usunięcie zabiera kolejkę
                    i czat, a jest ciche — więc mówimy o nim, dopóki da się temu
                    zapobiec (wystarczy wejść: wejście odsuwa licznik).
                  */}
                  {room.present === 0 && room.idleDeleteAt !== null && (
                    <span>zniknie {untilLabel(room.idleDeleteAt)}</span>
                  )}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * „za 1 h 20 min" dla terminu w przyszłości.
 *
 * Godziny i minuty, nie dni: cały zakres, o którym tu mówimy, to dwie godziny,
 * a „za 0 dni" nie jest zdaniem.
 */
function untilLabel(iso: string): string {
  const ms = Date.parse(iso) - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return "wkrótce";
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `za ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `za ${hours} h` : `za ${hours} h ${rest} min`;
}
